'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { registerSchema } from '@eliadmin/core/src/components/auth/register-schema';
import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import Link from '@eliadmin/core/src/components/link';
import {
  CaptchaAction,
  useRecaptcha,
} from '@eliadmin/core/src/components/providers/captcha-provider';
import {
  CheckboxField,
  PasswordField,
  TextField,
} from '@eliadmin/core/src/components/svetech/forms/fields';
import {
  AuthHeader,
  fieldGridClassName,
  fieldItemClassName,
  textLinkClassName,
} from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { registerSchema } from '@eliadmin/core/src/components/auth/register-schema';

type RegisterFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
};

/**
 * Registration (core logic unchanged): H2 title with the sign-in prompt, social sign-up, the
 * fields (names side by side from `md`), consents, the full-width submit and the terms note.
 */
export function RegisterForm({ initialSocialAuthProviders }: RegisterFormProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);
  const schema = registerSchema(formValidationMessages);

  const form = useForm<z.input<typeof schema>, any, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,
      'g-recaptcha-response': '',
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });
  const [gdpr, newsletter, newsletterReturnUrl] = useWatch({
    control: form.control,
    name: ['gdpr', 'newsletter', 'newsletter_return_url'],
  });
  const { executeRecaptcha } = useRecaptcha();

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {
      let captchaRes = undefined;
      if (executeRecaptcha) {
        captchaRes = await executeRecaptcha(CaptchaAction.REGISTER);
      }
      const resp = await register({ ...data, 'g-recaptcha-response': captchaRes!, locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const tosHref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.() ?? '/', locale, 'tos');
  const gdprHref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn('flex w-full flex-col gap-6 md:gap-8', 'ea-register-form')}
        onSubmit={handleSubmit}
      >
        <div className="ea-register-form__header">
          {/* Core prompt keys read „Zabudol som heslo Máte už svoj účet?“; text623 + sign-in. */}
          <AuthHeader title={t('text261')}>
            {t('text623')}{' '}
            <Link
              href={ROUTES.SIGN_IN() ?? '#'}
              className={cn(textLinkClassName, 'ea-register-form__link')}
              prefetch={false}
            >
              {t('text189')}
            </Link>
          </AuthHeader>
        </div>

        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          gdprAccepted={gdpr}
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={newsletter}
          newsletterReturnUrl={newsletterReturnUrl}
        />

        <div className="ea-register-form__content flex flex-col gap-4">
          <div className={fieldGridClassName}>
            <TextField
              control={form.control}
              name="name"
              label={t('text33')}
              placeholder={t('text33')}
              autoComplete="given-name"
              required
              className="ea-register-form__name-field"
            />
            <TextField
              control={form.control}
              name="surname"
              label={t('text34')}
              placeholder={t('text34')}
              autoComplete="family-name"
              required
              className="ea-register-form__surname-field"
            />
          </div>
          <TextField
            control={form.control}
            name="email"
            type="email"
            label={t('text35')}
            placeholder={t('text35')}
            autoComplete="email"
            className="ea-register-form__email-field"
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem
                required
                className={cn(fieldItemClassName, 'ea-register-form__phone-field')}
              >
                <FormLabel>{t('text36')}</FormLabel>
                <FormControl>
                  <PhoneInputField {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <PasswordField
            control={form.control}
            name="password"
            label={t('text191')}
            placeholder={t('text191')}
            autoComplete="new-password"
            hint={t('text630')}
            required
            className="ea-register-form__password-field"
          />
          <PasswordField
            control={form.control}
            name="password_confirmation"
            label={t('text263')}
            placeholder={t('text191')}
            autoComplete="new-password"
            required
            className="ea-register-form__password-confirmation-field"
          />
        </div>

        <div className="ea-register-form__footer flex flex-col gap-4">
          <CheckboxField control={form.control} name="tos" className="ea-register-form__tos-field">
            {t('text264')}{' '}
            <Link href={tosHref} className={textLinkClassName} prefetch={false} target="_blank">
              {t('text265')}
            </Link>
          </CheckboxField>
          <CheckboxField
            control={form.control}
            name="gdpr"
            className="ea-register-form__gdpr-field"
          >
            <Link href={gdprHref} className={textLinkClassName} prefetch={false} target="_blank">
              {t('text364')}
            </Link>
          </CheckboxField>
          <CheckboxField
            control={form.control}
            name="newsletter"
            className="ea-register-form__newsletter-field"
          >
            {t('text629')}
          </CheckboxField>
          <FormField
            control={form.control}
            name="g-recaptcha-response"
            render={() => (
              <FormItem className="w-full empty:hidden">
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            className="ea-register-form__submit-button w-full"
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
          <p className="svt-body-s text-center text-[#6b7681]">
            {t('text625')}{' '}
            <Link href={tosHref} className={textLinkClassName} prefetch={false}>
              {t('text626')}
            </Link>{' '}
            {t('text627')}{' '}
            <Link href={gdprHref} className={textLinkClassName} prefetch={false}>
              {t('text628')}
            </Link>
          </p>
        </div>
      </form>
    </Form>
  );
}
